import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { articles, BLOG_CATEGORIES, type BlogCategory } from "@/data/blog";
import { breadcrumbSchema, jsonLd, SITE_URL, absUrl } from "@/lib/seo";
import { internalPath } from "@/lib/paths";

export const Route = createFileRoute("/blogg")({
  head: () => ({
    meta: [
      { title: "Inspiration & Guider — Tailor's Barber" },
      {
        name: "description",
        content:
          "Guider om fade, herrfrisyrer, skäggvård och stylingtips från Tailor's Barber i Stockholm.",
      },
      { property: "og:title", content: "Inspiration & Guider — Tailor's Barber" },
      { property: "og:url", content: absUrl(`${SITE_URL}/blogg`) },
    ],
    links: [{ rel: "canonical", href: absUrl(`${SITE_URL}/blogg`) }],
    scripts: [
      jsonLd(
        breadcrumbSchema([
          { name: "Hem", url: "/" },
          { name: "Inspiration", url: "/blogg" },
        ]),
      ),
    ],
  }),
  component: BloggPage,
});

function BloggPage() {
  const [active, setActive] = useState<BlogCategory | "Alla">("Alla");
  const filtered = active === "Alla" ? articles : articles.filter((a) => a.category === active);

  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-8">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Inspiration</span>
        </nav>
        <div className="eyebrow">Inspiration</div>
        <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl max-w-3xl">Inspiration & Guider</h1>
        <p className="mt-5 text-lg text-foreground/75 max-w-2xl">
          Guider och tips från våra barberare — om fade, skäggvård, styling och trender i Stockholm.
        </p>
      </section>

      <section className="container-page pb-6">
        <div className="flex flex-wrap gap-2">
          {(["Alla", ...BLOG_CATEGORIES] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                active === c
                  ? "bg-foreground text-background border-foreground"
                  : "border-border text-foreground/70 hover:border-brass/50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="container-page pb-24">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <a
              key={a.slug}
              href={internalPath(`/${a.slug}`)}
              className="card-elev overflow-hidden flex flex-col group"
            >
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={a.hero}
                  alt={a.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5 flex flex-col gap-3 flex-1">
                <span className="text-[11px] font-medium uppercase tracking-widest text-brass/80 border border-brass/30 rounded px-2 py-0.5 self-start">
                  {a.category}
                </span>
                <h2 className="text-lg leading-tight font-semibold">{a.title}</h2>
                <p className="text-sm text-muted-foreground line-clamp-3">{a.excerpt}</p>
                <span className="mt-auto pt-2 text-sm font-medium text-brass">
                  Läs guide →
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>
    </SiteShell>
  );
}
